import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { buildInfo } from '../../build-info';
import { environment } from '../../environments/environment';

export interface ServerVersion {
  version: string;
  commit: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class VersionService {
  private readonly http = inject(HttpClient);

  /** Frontend version, stamped from the release tag at build time ('0.0.0-dev' in dev). */
  public readonly appVersion = buildInfo.version;
  /** Short commit the frontend was built from ('local' in dev). */
  public readonly appCommit = buildInfo.commit;
  /** ISO build timestamp ('' in dev). */
  public readonly buildDate = buildInfo.buildDate;

  public getVersion(): string {
    return this.appVersion;
  }

  /** Backend version, fetched at runtime; falls back to 'unavailable' on error. */
  public getServerVersion(): Observable<ServerVersion> {
    return this.http.get<ServerVersion>(`${environment.apiUrl}/version`).pipe(
      catchError(() => of<ServerVersion>({ version: 'unavailable', commit: null }))
    );
  }

  /** Frontend app version with its commit, the same way the About dialog renders it. */
  public get appDisplay(): string {
    return this.appCommit && this.appCommit !== 'local'
      ? `${this.appVersion} (${this.appCommit})`
      : this.appVersion;
  }

  /**
   * "App + Server" version string mirroring the About dialog, e.g.
   * "App: 1.2.3 (abc123) · Server: 1.2.0 (def456)". Used to stamp an issue's reported/fixed version.
   */
  public getCombinedVersion(): Observable<string> {
    return this.getServerVersion().pipe(
      map(v => {
        const server = v.commit ? `${v.version} (${v.commit})` : v.version;
        return `App: ${this.appDisplay} · Server: ${server}`;
      })
    );
  }
}
